import { useCallback, useEffect, useMemo, useState } from "react";
import Flex from "../../common/Flex";
import Slider from "../../common/Slider";
import Typography from "../../common/Typography";
import useDebounceCallback from "@/shared/hooks/useDebouncCallback";
import type { FilterComponent } from "../filter.types";
import config from "@/configs/config";
import { useClientSearchParams } from "@/shared/hooks/useClientSearchParams";

export type PriceRange = {
  min: number;
  max: number;
  step?: number;
  unit?: string;
  samples?: number[];
};

const DEFAULT_PRICE_RANGE: PriceRange = {
  min: 0,
  max: 999999,
  step: 1,
  unit: "",
};

const PRICE_FILTER_DEBOUNCE_DELAY = 500;
const PRICE_SLIDER_STEP = 10000;

const PriceFilter: FilterComponent<PriceRange> = ({
  value,
  applyFilter,
  filterName,
  currentValue,
}) => {
  const clientSearchParams = useClientSearchParams();
  const defaultRange: PriceRange = {
    min: value?.min ?? DEFAULT_PRICE_RANGE.min,
    max: value?.max ?? DEFAULT_PRICE_RANGE.max,
    step: value?.step ?? DEFAULT_PRICE_RANGE.step,
    unit: value?.unit ?? DEFAULT_PRICE_RANGE.unit,
    samples: value?.samples ?? [],
  };

  const currentState =
    currentValue ?? clientSearchParams.get(filterName || "price");

  const [currentMin, currentMax] = currentState?.split("-") || "";

  const [priceRange, setPriceRange] = useState<number[]>([
    +currentMin || defaultRange.min,
    +currentMax || defaultRange.max,
  ]);
  const histogram = useMemo(
    () =>
      buildPriceHistogram(
        defaultRange.samples ?? [],
        defaultRange.min,
        defaultRange.max,
      ),
    [defaultRange.max, defaultRange.min, defaultRange.samples],
  );
  const maxDensity = Math.max(...histogram, 1);

  // Use unit from value prop (defaultRange)
  const currencySymbol = defaultRange.unit;

  const debouncedFilterUpdate = useDebounceCallback((range: number[]) => {
    const filterValue = `${range[0]}-${range[1]}`;
    applyFilter(filterValue)();
  }, PRICE_FILTER_DEBOUNCE_DELAY);

  const updatePriceRange = (newRange: number[]) => {
    setPriceRange(newRange);
    debouncedFilterUpdate(newRange);
  };

  const resetPriceRange = useCallback(() => {
    setPriceRange([defaultRange.min, defaultRange.max]);
  }, [defaultRange.max, defaultRange.min]);

  useEffect(() => {
    if (!currentState) {
      resetPriceRange();
    }
  }, [currentState, resetPriceRange]);

  const formatPrice = (value: number): string =>
    `${value.toLocaleString("fa-IR")} ${currencySymbol}`;

  return (
    <Flex direction="col" className="py-5 w-full" gap="4">
      <div
        className="grid h-12 w-full items-end gap-px"
        dir="ltr"
        style={{
          gridTemplateColumns: `repeat(${histogram.length}, minmax(0, 1fr))`,
        }}
      >
        {histogram.map((count, index) => {
          const height = count ? Math.max(10, (count / maxDensity) * 100) : 3;
          return (
            <span
              key={index}
              className="rounded-t-sm bg-primary/45 transition-[height] duration-150"
              style={{ height: `${height}%` }}
              title={`${count} بلیط`}
            />
          );
        })}
      </div>
      <Slider
        dir={config.dir}
        onValueChange={updatePriceRange}
        value={priceRange}
        min={defaultRange.min}
        max={defaultRange.max}
        step={defaultRange.step ?? PRICE_SLIDER_STEP}
        classNames={{
          range: "bg-primary",
          thumb: {
            root: "border-[3px] border-primary",
            values: "text-sm",
          },
        }}
      />
      <Flex direction="row" alignItems="center" justifyContent="between">
        <Typography variant="body3">{formatPrice(priceRange[0])}</Typography>
        <Typography variant="body3">{formatPrice(priceRange[1])}</Typography>
      </Flex>
    </Flex>
  );
};

export default PriceFilter;

function buildPriceHistogram(samples: number[], min: number, max: number) {
  const bucketCount = 28;
  const bucketSize = Math.max(1, (max - min) / bucketCount);
  const histogram = Array.from({ length: bucketCount }, () => 0);

  samples.forEach((price) => {
    if (!Number.isFinite(price) || price < min || price > max) return;

    const bucket = Math.min(
      Math.floor((price - min) / bucketSize),
      bucketCount - 1,
    );
    histogram[bucket] += 1;
  });

  return histogram;
}
